import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import './LearningPage.css';

const LearningPage = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoStatus, setVideoStatus] = useState({});
  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState({});
  const [userAnswer, setUserAnswer] = useState('');
  const [learningProgress, setLearningProgress] = useState({});
  const videoRef = useRef(null);
  const lastProgressUpdate = useRef(0);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (selectedVideo) {
      loadLearningProgress();
    }
  }, [selectedVideo]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const response = await api.get('/learning/courses');
      if (response.data && response.data.success) {
        setCourses(response.data.data.courses || []);
      }
    } catch (error) {
      console.error('获取课程失败:', error);
      alert('获取课程失败');
    } finally {
      setLoading(false);
    }
  };

  const loadLearningProgress = async () => {
    try {
      const response = await api.get(`/learning/progress/${selectedVideo.id}`);
      if (response.data && response.data.success) {
        setLearningProgress(response.data.data || { watched_seconds: 0 });
        // 跳转到上次观看的位置
        if (videoRef.current && response.data.data.watched_seconds > 0) {
          videoRef.current.currentTime = response.data.data.watched_seconds;
        }
      }
    } catch (error) {
      console.error('获取学习进度失败:', error);
    }
  };

  const handleVideoPress = (course, video) => {
    setSelectedCourse(course);
    setSelectedVideo(video);
  };

  const handleVideoStatusUpdate = async () => {
    if (!videoRef.current) return;
    
    const status = {
      isLoaded: true,
      positionMillis: videoRef.current.currentTime * 1000,
      durationMillis: videoRef.current.duration * 1000,
      isPlaying: !videoRef.current.paused
    };
    setVideoStatus(status);
    
    // 每10秒保存一次学习进度
    if (status.isLoaded && status.positionMillis && status.durationMillis) {
      const currentProgress = Math.floor(status.positionMillis / 1000);
      if (currentProgress - lastProgressUpdate.current >= 10) {
        lastProgressUpdate.current = currentProgress;
        await saveLearningProgress(currentProgress);
      }
      
      // 随机弹出计算题
      if (status.isPlaying && Math.random() < 0.005) { // 0.5%的概率
        generateQuiz();
      }
    }
  };

  const saveLearningProgress = async (watchedSeconds) => {
    if (!selectedVideo) return;
    
    try {
      await api.post('/learning/progress', {
        video_id: selectedVideo.id,
        watched_seconds: watchedSeconds
      });
      setLearningProgress(prev => ({ ...prev, watched_seconds: watchedSeconds }));
    } catch (error) {
      console.error('保存学习进度失败:', error);
    }
  };

  const generateQuiz = () => {
    // 生成简单的计算题
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const operators = ['+', '-', '*'];
    const operator = operators[Math.floor(Math.random() * operators.length)];
    
    let answer;
    switch (operator) {
      case '+':
        answer = num1 + num2;
        break;
      case '-':
        answer = num1 - num2;
        break;
      case '*':
        answer = num1 * num2;
        break;
    }
    
    setQuiz({
      question: `${num1} ${operator} ${num2} = ?`,
      answer: answer.toString()
    });
    setShowQuiz(true);
    
    // 暂停视频
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleQuizSubmit = () => {
    if (userAnswer === quiz.answer) {
      alert('正确，继续学习');
      setShowQuiz(false);
      setUserAnswer('');
      // 继续播放视频
      if (videoRef.current) {
        videoRef.current.play();
      }
    } else {
      alert('错误，请重新回答');
    }
  };

  const handleVideoEnd = async () => {
    if (selectedVideo) {
      // 保存完整观看记录
      await saveLearningProgress(selectedVideo.duration || 0);
      // 记录学习完成
      try {
        await api.post('/learning/records', {
          course_id: selectedCourse.id,
          video_id: selectedVideo.id
        });
      } catch (error) {
        console.error('记录学习完成失败:', error);
      }
    }
  };

  const handleBack = () => {
    // 保存当前进度
    if (videoRef.current) {
      const currentProgress = Math.floor(videoRef.current.currentTime);
      saveLearningProgress(currentProgress);
    }
    setSelectedVideo(null);
    setSelectedCourse(null);
  };

  if (loading) {
    return (
      <div className="learning-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">加载中...</p>
        </div>
      </div>
    );
  }

  if (selectedVideo) {
    return (
      <div className="learning-container">
        <div className="video-container">
          <button className="back-button" onClick={handleBack}>
            返回
          </button>
          <h2 className="video-title">{selectedVideo.name}</h2>
          <div className="video-wrapper">
            {videoLoading ? (
              <div className="video-loading">
                <div className="loading-spinner"></div>
                <p className="loading-text">视频加载中...</p>
              </div>
            ) : (
              <video
                ref={videoRef}
                className="video"
                src={selectedVideo.url}
                controls={false}
                onLoadedData={() => setVideoLoading(false)}
                onTimeUpdate={handleVideoStatusUpdate}
                onEnded={handleVideoEnd}
                onError={(e) => {
                  // 捕获视频加载错误，避免控制台报错
                  console.log('视频加载错误:', e);
                  setVideoLoading(false);
                }}
              />
            )}
          </div>
          <div className="video-controls">
            <button
              className="control-button"
              onClick={() => {
                if (videoRef.current) {
                  if (!videoRef.current.paused) {
                    videoRef.current.pause();
                  } else {
                    videoRef.current.play();
                  }
                }
              }}
            >
              {videoStatus.isPlaying ? '暂停' : '播放'}
            </button>
            <span className="progress-text">
              {videoStatus.positionMillis ? Math.floor(videoStatus.positionMillis / 1000) : 0} / {selectedVideo.duration || 0} 秒
            </span>
          </div>
          <h3 className="course-name">{selectedCourse.name}</h3>
          <p className="video-description">{selectedVideo.description || '无描述'}</p>
        </div>
        
        {/* 计算题弹窗 */}
        {showQuiz && (
          <div className="quiz-overlay">
            <div className="quiz-container">
              <h3 className="quiz-title">请回答以下问题</h3>
              <p className="quiz-question">{quiz.question}</p>
              <input
                className="quiz-input"
                type="number"
                onChange={(e) => setUserAnswer(e.target.value)}
                value={userAnswer}
              />
              <button className="quiz-button" onClick={handleQuizSubmit}>
                提交
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="learning-container">
      <div className="header">
        <h1 className="header-title">学习中心</h1>
      </div>
      <div className="content">
        {courses.map(course => (
          <div key={course.id} className="course-card">
            <h2 className="course-title">{course.name}</h2>
            <span className="course-type">{course.type}</span>
            <p className="course-description">{course.description || '无描述'}</p>
            <h3 className="video-list-title">视频列表：</h3>
            {course.videos && course.videos.map(video => (
              <div
                key={video.id}
                className="video-item"
                onClick={() => handleVideoPress(course, video)}
              >
                <h4 className="video-item-title">{video.name}</h4>
                <span className="video-item-duration">{video.duration} 秒</span>
                {learningProgress[video.id] && (
                  <span className="video-progress">
                    已观看: {learningProgress[video.id]?.watched_seconds || 0} 秒
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LearningPage;